import { ArrowDownTrayIcon, ArrowRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';
import {
	GQLOrderFragment,
	GQLOrderStatusType,
	GQLPaymentMethodType,
	GQLPaymentStatusType,
	GQLSupplementaryPaymentMethodType,
	GQLSupplementaryPaymentStatusType,
	GQLVaultedCardFragment,
} from '@theprintbar/graphql';
import {
	WebRoutes,
	cn,
	getCurrencyString,
	getCurrencyValue,
	getOrderPayment,
	getPaymentAmount,
	getReadableDate,
	getStoreOrderPricing,
	getWebOrderPricing,
	withDomain,
} from '@theprintbar/shared';
import { PaymentMethodIcon } from '../Icons';

interface OrderPaymentProps {
	order: GQLOrderFragment;
	webDomain?: string;
	refundReceiptLink?: (id: string) => string;
	viewInvoiceLink?: string;
	className?: string;
	staff?: boolean;
}

const powerboardChargeLink = (chargeId: string) => `https://powerboard.commbank.com.au/v2/charges?charges_id=${chargeId}`;

// Free orders are recorded with a placeholder in place of a real Powerboard charge id.
const isPowerboardCharge = (chargeId?: string | null): chargeId is string => !!chargeId && chargeId.trim().toLowerCase() !== 'free';

export function OrderPayment({ order, refundReceiptLink, webDomain, viewInvoiceLink, className, staff }: OrderPaymentProps) {
	const payment = getOrderPayment(order);
	const { paid, owed } = order.store ? getStoreOrderPricing(order) : getWebOrderPricing(order);

	const payments: Array<{
		charge: string;
		method: GQLPaymentMethodType | GQLSupplementaryPaymentMethodType;
		amount: number;
		vaultedCard?: GQLVaultedCardFragment | null;
		powerboardChargeId?: string | null;
	}> = [];
	if (payment?.status === GQLPaymentStatusType.Complete && (!!payment.powerboardChargeId || !!payment.transactionReference)) {
		payments.push({
			charge: payment.powerboardChargeId || payment.transactionReference || 'unknown',
			method: payment.method,
			amount: getPaymentAmount(payment).total,
			vaultedCard: payment.vaultedCard,
			powerboardChargeId: payment.powerboardChargeId,
		});
	}
	payment?.supplementaryPayments?.forEach(sp => {
		if (sp.status === GQLSupplementaryPaymentStatusType.Complete && (!!sp.powerboardChargeId || !!sp.transactionReference)) {
			const ref = sp.powerboardChargeId || sp.transactionReference || 'unknown';
			const existingIndex = payments.findIndex(p => p.charge === ref);
			if (existingIndex >= 0) {
				payments[existingIndex].amount += getCurrencyValue(sp.amount);
			} else {
				payments.push({
					charge: ref,
					method: sp.method,
					amount: getCurrencyValue(sp.amount),
					vaultedCard: sp.vaultedCard,
					powerboardChargeId: sp.powerboardChargeId,
				});
			}
		}
	});

	return (
		<div className={cn('text-xs', className)}>
			<div className="mb-2 flex items-center gap-4">
				<h5 className="uppercase">Payment Details</h5>
				{!!viewInvoiceLink && (
					<Link className="flex underline items-center gap-1" to={viewInvoiceLink} target="_blank">
						View Invoice
						<ArrowRightIcon className="h-3 w-3" />
					</Link>
				)}
			</div>

			{!payment ? (
				<p>No payment details available.</p>
			) : payment.status === GQLPaymentStatusType.Failed ? (
				<p>Payment failed, please try again.</p>
			) : order.status === GQLOrderStatusType.PendingPayment ? (
				<p>Order is pending payment, please use the button above.</p>
			) : order.status === GQLOrderStatusType.Cancelled && !payment?.vaultedCard ? (
				<p>Order cancelled prior to payment.</p>
			) : null}
			{payments.map(pay => (
				<div key={pay.charge}>
					<div className="flex items-center gap-2">
						<PaymentMethodIcon paymentMethod={pay.method} cardType={pay.vaultedCard?.type} className="h-6" />
						<p className="font-bold">
							{pay.vaultedCard && `Card ending in ${pay.vaultedCard.lastFour}`}- {getCurrencyString(pay.amount)}
						</p>
					</div>
					{!!staff && isPowerboardCharge(pay.powerboardChargeId) && (
						<a
							className="flex items-center underline text-green-800"
							href={powerboardChargeLink(pay.powerboardChargeId)}
							target="_blank"
							rel="noreferrer"
						>
							View in Powerboard
							<ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
						</a>
					)}
				</div>
			))}

			{!!refundReceiptLink &&
				order.paymentRefunds?.map(refund => (
					<Link
						key={refund.id}
						className="mt-2 flex items-center underline text-green-800"
						to={refundReceiptLink(refund.id)}
						target="_blank"
					>
						<ArrowDownTrayIcon className="h-4 w-4 mr-2" />
						{getCurrencyString(refund.amount)} - {getReadableDate(refund.createdAt)} Refund Receipt
					</Link>
				))}

			{!!owed && !order.store && (
				<Link
					className="mt-2 flex items-center underline text-green-800"
					to={withDomain(WebRoutes.PaymentRequest.Index(order.id, order.viewId), webDomain)}
					target="_blank"
				>
					Visit payment request page
					<ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
				</Link>
			)}
		</div>
	);
}
